import React, { ReactNode, useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  tableCellClasses,
  TableContainer,
  Box,
  styled,
  Typography,
} from "@mui/material";

import SearchInput from "../../../../components/SearchInput";
import FilterButton from "./FilterButton";
import SortButton from "./SortButton";

import { multiSort } from "../../../../helpers";
import { TABLE_COLORS } from "../../../../constants";
// import LockOverlay from "../../../../components/LockOverlay";

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) =>
    prop !== "noBorderTop" && prop !== "headerBackground" && prop !== "wrap",
})<{ noBorderTop?: boolean; headerBackground?: string; wrap?: boolean }>(
  ({ noBorderTop, headerBackground, wrap }) => ({
    [`&.${tableCellClasses.root}`]: {
      padding: "0px 4px",
      borderColor: "#dcdddd",
      whiteSpace: wrap ? "wrap" : "nowrap",
      overflow: wrap ? "visible" : "hidden",
      textOverflow: wrap ? "clip" : "ellipsis",
      "&:not(:last-child)": {
        borderRight: "1px solid #dcdddd",
      },
      "& .MuiTableSortLabel-root .MuiSvgIcon-root": {
        width: "12px",
        height: "12px",
      },
    },
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: headerBackground
        ? `${headerBackground} !important`
        : `${TABLE_COLORS.headerBackground} !important`,
      color: "#383838",
      height: "24px",
      borderTop: noBorderTop ? "unset" : "1px solid #dcdddd",
      borderBottom: "1px solid #dcdddd",
      fontSize: "11px",
      fontWeight: 600,
      lineHeight: "18px",
    },
    [`&.${tableCellClasses.body}`]: {
      color: "#000000",
      height: "30px",
      fontSize: "11px",
      fontWeight: 600,
      lineHeight: "18px",
    },
  }),
);

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "noBorderBottom",
})<{ noBorderBottom?: boolean }>(({ noBorderBottom }) => ({
  backgroundColor: "#fafafa",

  "&:nth-of-type(odd)": {
    backgroundColor: "#FFFFFF",
  },
  ...(noBorderBottom && {
    "&:last-child td": {
      borderBottom: "0 !important",
    },
  }),
}));

export type TOrder = "asc" | "desc";

export interface ISortOption<T> {
  accessorKey: keyof T;
  order: TOrder;
}

export type TSortableColumn<T> = {
  header: string;
  accessorKey: keyof T;
  align?: "left" | "center" | "right";
  hidden?: boolean;
  formatter?: (data: T) => string | React.ReactNode;
  html?: (data: T) => ReactNode;
  width?: number | string;
  wrap?: boolean;
};

export interface ISortableTableProps<T> {
  columns: TSortableColumn<T>[];
  data: T[];
  maxHeight?: number | string;
  noFilters?: boolean;
  noBorderTop?: boolean;
  noBorderBottom?: boolean;
  noDefaultSort?: boolean;
  noSearch?: boolean;
  showIndexColumn?: boolean;
  headerBackground?: string;
  defaultSort?: ISortOption<T>;
  // lock?: boolean;
  asin?: string;
  lookupId?: string;
  title?: string;
  // numberOfRowsToShow?: number;
}

export const SortableTable = <T,>({
  columns: defaultColumns,
  data,
  maxHeight = "500px",
  noFilters,
  noBorderTop,
  noBorderBottom,
  noDefaultSort,
  defaultSort,
  noSearch,
  showIndexColumn,
  headerBackground,
  // lock = false,
  asin,
  lookupId,
  title = "",
  // numberOfRowsToShow: defaultNumberOfRowsToShow = 0,
}: ISortableTableProps<T>) => {
  const [search, setSearch] = useState<string>("");
  const [columns, setColumns] = useState<TSortableColumn<T>[]>(defaultColumns);
  const firstVisibleColumn = defaultColumns.find((col) => !col.hidden);
  const [sortOptions, setSortOptions] = useState<ISortOption<T>[]>(
    noDefaultSort
      ? []
      : defaultSort
        ? [defaultSort]
        : firstVisibleColumn
          ? [
              {
                accessorKey: firstVisibleColumn.accessorKey,
                order: "asc",
              },
            ]
          : [],
  );
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number>();
  const [updatedFromNow, setUpdatedFromNow] = useState<string>();
  // const numberOfRowsToShow = lock ? defaultNumberOfRowsToShow : 0;

  useEffect(() => {
    setLastUpdatedAt(Date.now());
  }, [data]);

  useEffect(() => {
    if (!noSearch) return;

    const id = setInterval(() => {
      setUpdatedFromNow(moment(lastUpdatedAt).fromNow());
    }, 1000);

    return () => {
      clearInterval(id);
    };
  }, [setUpdatedFromNow, lastUpdatedAt, noSearch]);

  const visibleColumns = useMemo(
    () => columns.filter((col) => !col.hidden),
    [columns],
  );

  const rows = useMemo(() => {
    let res = [...data];

    // sorting
    res = multiSort(res, sortOptions);

    // filtering
    if (search) {
      res = res.filter(
        (row: T) =>
          !!Object.values(row as object).filter((val) =>
            String(val).includes(search),
          ).length,
      );
    }

    return res;
  }, [data, search, sortOptions]);

  const handleSort = (accessorKey: keyof T) => {
    const option = sortOptions.find((o) => o.accessorKey === accessorKey);
    if (option) {
      const order = option.order === "asc" ? "desc" : "asc";
      setSortOptions([{ accessorKey, order }]);
    } else {
      setSortOptions([{ accessorKey, order: "asc" }]);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleFilterChange = (col: TSortableColumn<T>) => {
    setColumns((prev) =>
      prev.map((c) => {
        if (c.accessorKey === col.accessorKey) {
          return {
            ...c,
            hidden: !col.hidden,
          };
        } else {
          return c;
        }
      }),
    );
  };

  const renderTableCell = (row: T, column: TSortableColumn<T>) => {
    let value: any = row[column.accessorKey];
    const defaultColumn = defaultColumns.find(
      (col) => col.accessorKey === column.accessorKey,
    );
    if (defaultColumn?.formatter) {
      value = defaultColumn.formatter(row);
    }
    if (defaultColumn?.html) {
      return defaultColumn.html({ ...row, [column.accessorKey]: value });
    } else {
      return value;
    }
  };

  return (
    <>
      {!noFilters && (
        <Box
          display={"flex"}
          justifyContent={"flex-end"}
          alignItems={"flex-end"}
          gap={"4px"}
          padding={"4px"}
        >
          {!noSearch ? (
            <SearchInput
              placeholder={"Search"}
              onChange={handleSearchChange}
              value={search}
            />
          ) : (
            <Typography variant="body2" sx={{ flexGrow: 1, color: "#717680" }}>
              Last updated at: {updatedFromNow}
            </Typography>
          )}
          <FilterButton columns={columns} onChange={handleFilterChange} />
          <SortButton
            columns={columns}
            sortOptions={sortOptions}
            onChange={setSortOptions}
          />
        </Box>
      )}
      <TableContainer sx={{ maxHeight, width: "100%" }}>
        <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <StyledTableRow
              sx={
                headerBackground
                  ? { backgroundColor: `${headerBackground} !important` }
                  : {
                      backgroundColor: `${TABLE_COLORS.headerBackground} !important`,
                    }
              }
            >
              {showIndexColumn && (
                <StyledTableCell align="center" width="34px">
                  #
                </StyledTableCell>
              )}
              {visibleColumns.map((col: TSortableColumn<T>) => {
                const order = sortOptions.find(
                  (o) => o.accessorKey === col.accessorKey,
                )?.order;
                return (
                  <StyledTableCell
                    key={`table-head-cell-${col.header}`}
                    sortDirection={order ?? false}
                    align="center"
                    width={col.width}
                    noBorderTop={noBorderTop}
                    headerBackground={headerBackground}
                    sx={col.width ? { width: col.width, minWidth: col.width, maxWidth: col.width } : undefined}
                  >
                    <TableSortLabel
                      active={!!order}
                      direction={order ?? "asc"}
                      onClick={() => handleSort(col.accessorKey)}
                      hideSortIcon
                    >
                      {col.header}
                    </TableSortLabel>
                  </StyledTableCell>
                );
              })}
            </StyledTableRow>
          </TableHead>

          {rows.length > 0 && (
            <TableBody>
              {rows.map((row: T, index) => (
                <StyledTableRow
                  key={`table-row-${index}`}
                  noBorderBottom={noBorderBottom}
                >
                  {showIndexColumn && (
                    <StyledTableCell align={"center"} width={"34px"}>
                      {index + 1}
                    </StyledTableCell>
                  )}
                  {visibleColumns.map((col: TSortableColumn<T>) => {
                    return (
                      <StyledTableCell
                        key={`table-row-cell-${col.header}`}
                        align={col.align || "center"}
                        width={col.width}
                        wrap={col.wrap}
                        sx={col.width ? { width: col.width, minWidth: col.width, maxWidth: col.width } : undefined}
                      >
                        {renderTableCell(row, col)}
                      </StyledTableCell>
                    );
                  })}
                </StyledTableRow>
              ))}
            </TableBody>
          )}
          {/* {rows.length > numberOfRowsToShow && (
            <TableBody sx={{ position: "relative" }}>
              {rows.slice(numberOfRowsToShow).map((row: T, index) => (
                <StyledTableRow
                  key={`table-row-${numberOfRowsToShow + index}`}
                  noBorderBottom={noBorderBottom}
                >
                  {showIndexColumn && (
                    <StyledTableCell align={"center"} width={"34px"}>
                      {numberOfRowsToShow + index + 1}
                    </StyledTableCell>
                  )}
                  {visibleColumns.map((col: TSortableColumn<T>) => {
                    return (
                      <StyledTableCell
                        key={`table-row-cell-${col.header}`}
                        align={col.align || "center"}
                        width={col.width}
                        wrap={col.wrap}
                      >
                        {renderTableCell(row, col)}
                      </StyledTableCell>
                    );
                  })}
                </StyledTableRow>
              ))}
              {lock && asin && (
                <LockOverlay title={title} asin={asin} lookupId={lookupId} />
              )}
            </TableBody>
          )} */}
        </Table>
      </TableContainer>
    </>
  );
};

export default SortableTable;
